/**
 * Walkover Confirmation Modal
 * Confirms that a team wants to forfeit a match (give WO)
 */

"use client";

import { submitWalkover } from "@/actions/match";
import { useAuth } from "@/contexts/AuthContext";
import { logMatchEvent } from "@/lib/analytics";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { addToast } from "@heroui/toast";
import { useTranslations } from "next-intl";
import { useState } from "react";

interface WalkoverConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  matchId?: string;
  forfeitingTeamId?: string;
  opponentName?: string;
}

export function WalkoverConfirmModal({
  isOpen,
  onClose,
  onSuccess,
  matchId,
  forfeitingTeamId,
  opponentName,
}: WalkoverConfirmModalProps) {
  const t = useTranslations();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    setIsSubmitting(true);

    if (!matchId || !forfeitingTeamId || !user?.id) {
      setError(t("matches.failedToSubmitResult"));
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await submitWalkover({
        matchId,
        forfeitingTeamId,
        submittingUserId: user.id,
      });

      if (response.success) {
        addToast({
          title: t("matches.walkoverSubmitted"),
          description: t("matches.walkoverSubmittedSuccess"),
          color: "success",
        });
        logMatchEvent("walkover", matchId);
        onSuccess?.();
        onClose();
      } else {
        setError(response.error || t("matches.failedToSubmitResult"));
      }
    } catch (err) {
      console.error("Failed to submit walkover:", err);
      setError(t("matches.failedToSubmitResult"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      placement="center"
      onClose={handleClose}
      isDismissable={!isSubmitting}
      isKeyboardDismissDisabled={isSubmitting}
      hideCloseButton
      backdrop="opaque"
      size="md"
      className="medieval-modal"
      classNames={{
        wrapper: "z-[200] max-sm:items-start",
        backdrop: "bg-black/80",
        base: "bg-[var(--bg-card)] border border-[var(--border-gold)] shadow-2xl max-sm:m-0 max-sm:rounded-none max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:w-full max-sm:max-w-full max-sm:border-0",
        header: "border-b border-[var(--border-subtle)]",
        body: "py-6",
        footer:
          "border-t border-[var(--border-subtle)] max-sm:sticky max-sm:bottom-0 max-sm:bg-[var(--bg-card)]",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 text-primary font-cinzel">
          <h3 className="text-xl">
            <span aria-hidden="true">🙌</span> {t("matches.walkoverConfirmTitle")}
          </h3>
        </ModalHeader>
        <ModalBody>
          <p className="text-sm text-white/70">
            {t("matches.walkoverConfirmMessage", {
              opponentName: opponentName || "",
            })}
          </p>
          {error && (
            <div
              role="alert"
              className="mt-4 p-3 rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800"
            >
              <p className="text-sm text-danger-600 dark:text-danger-400">
                {error}
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="light"
            onPress={handleClose}
            isDisabled={isSubmitting}
            className="font-semibold"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onPress={handleConfirm}
            isLoading={isSubmitting}
            color="warning"
            className="font-semibold"
          >
            {t("matches.giveWalkover")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
